import * as Sentry from '@sentry/react';
import { useState } from 'react';

import { useUpdateScheduleDateDetailsMutation } from '@mutations/UserScheduleSubmissionsMutations';

import Button from '@components/buttons/Button';
import { FormLabel } from '@components/form/FormLabel';
import { Input } from '@components/form/Input';
import { Popup } from '@components/layout/Popup/Popup';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

interface AddCastSizePopupContentProps {
  scheduleDateDetailsId?: string;
  onClose: () => void;
}

export const AddCastSizePopupContent = ({
  scheduleDateDetailsId,
  onClose,
}: AddCastSizePopupContentProps) => {
  const { updateScheduleDateDetailsAsync } = useUpdateScheduleDateDetailsMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [castSize, setCastSize] = useState<number | undefined>(undefined);
  const [castSizeError, setCastSizeError] = useState(false);

  const canSubmit = !!castSize && castSize > 0 && !castSizeError && !isSubmitting;

  const handleSubmit = async () => {
    try {
      if (!canSubmit) return;

      setIsSubmitting(true);

      if (scheduleDateDetailsId) {
        await updateScheduleDateDetailsAsync({
          scheduleDateDetailsId,
          castSize,
        });
      }

      onClose();
    } catch (e: any) {
      Sentry.captureException(e);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <TitleToolbar text="Add cast size" size="md" />
      <div>
        <FormLabel text="Cast size" />
        <Input
          placeholder="eg. 4"
          type="number"
          min={1}
          style={{
            background: '#000',
            border: castSizeError
              ? '1px solid var(--color-red)'
              : '1px solid rgba(255, 255, 255, 0.2)',
          }}
          onChange={(e) => {
            try {
              const value = e.target.value.trim();
              setCastSizeError(false);

              if (value) {
                const parsedValue = parseInt(value);

                if (isNaN(parsedValue) || parsedValue < 1) {
                  setCastSize(undefined);
                  setCastSizeError(true);
                  return;
                }

                setCastSize(parsedValue);
              } else {
                setCastSize(undefined);
              }
            } catch (e) {
              setCastSize(undefined);
              setCastSizeError(true);
            }
          }}
        />
      </div>
      <div className="mt-2">
        <Button roundness="rounded" disabled={!canSubmit || isSubmitting} onClick={handleSubmit}>
          Save
        </Button>
      </div>
    </div>
  );
};

interface AddCastSizePopupProps extends AddCastSizePopupContentProps {
  isVisible: boolean;
}

export const AddCastSizePopup = ({ isVisible, ...props }: AddCastSizePopupProps) => {
  return (
    <Popup isVisible={isVisible} onClose={props.onClose}>
      {isVisible && (
        <Popup.Content>
          <AddCastSizePopupContent {...props} />
        </Popup.Content>
      )}
    </Popup>
  );
};
